import { yupResolver } from '@hookform/resolvers/yup';
import { Button, FormControl, FormHelperText, TextField, Typography } from '@mui/material';
import { useEffect, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
// import InputMask from 'react-input-mask';
import { Oval } from 'react-loader-spinner';
import { useNavigate } from 'react-router-dom';

import { useAppDispatch } from 'store/store';
import { resetTickets } from 'store/ticket-service/ticketSlice';
import {
	deleteCustomer,
	deleteUserAccount,
	deleteUserIcon,
	updateUserIcon,
	updateUserProfile,
} from 'store/user-service/asyncActions';

import { ROUTES } from 'core/constants/routes';
import { NamespacesList } from 'core/enums/common';
import { errorNotify, successNotify } from 'core/helpers/toasts';
import { userProfileSchema } from 'core/helpers/yupSchemas';
import { UserData } from 'core/types/base';

import { ChangePasswordModal } from 'components/Profile/ChangePasswordModal';
import { DeleteAccountModal } from 'components/Profile/DeleteAccountModal';
import { fieldNames } from 'components/Profile/helpers';
import { SubmitButton } from 'components/Profile/styled';

import { ReactComponent as Camera } from 'assets/icons/camera.svg';
import { ReactComponent as Trash } from 'assets/icons/trash.svg';

import { ChangeEmail } from './ChangeEmail';
import * as Styled from './styled';

export const User = () => {
	const dispatch = useAppDispatch();
	const navigate = useNavigate();

	const { t: commonTranslate } = useTranslation(NamespacesList['common']);
	const { t: signUpPageTranslate } = useTranslation(NamespacesList['signUpPage']);
	const { t: profileTranslate } = useTranslation(NamespacesList['profilePage']);

	const inputRef = useRef<null | HTMLInputElement>(null);

	const [user, setUser] = useState(JSON.parse(localStorage.getItem('user') || ''));
	const [icon, setIcon] = useState(user?.avatarLink);
	const [isIconUploading, setIsIconUploading] = useState(false);
	const [isOpenDeleteModal, setIsOpenDeleteModal] = useState(false);
	const [isOpenChangePasswordModal, setIsOpenChangePasswordModal] = useState(false);
	const [updatingLoading, setUpdatingLoading] = useState(false);

	const {
		handleSubmit,
		register,
		formState: { errors, isDirty },
		setValue,
		reset,
	} = useForm({
		resolver: yupResolver(userProfileSchema()),
	});

	const getUserData = () => {
		fieldNames.forEach((fieldName) => {
			if (fieldName === 'phoneNumber' && user[fieldName]) {
				setValue(
					fieldName,
					user[fieldName].replace(/^(\+\d{3})(\d{2})(\d{3})(\d{2})(\d{2})$/, '$1 $2 $3 $4 $5')
				);
			} else {
				setValue(fieldName, user[fieldName]);
			}
		});
	};

	useEffect(() => {
		getUserData();
	}, [user]);

	const getBase64 = (file: File): Promise<string> => {
		return new Promise((resolve, reject) => {
			const reader = new FileReader();

			reader.onload = () => {
				const base64Result = reader.result as string;
				resolve(base64Result);
			};

			reader.onerror = (error) => {
				reject(error);
			};

			reader.readAsDataURL(file);
		});
	};

	const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
		try {
			const selectedFile = e.target.files?.[0];

			if (!selectedFile) {
				// Réinitialiser l'input si aucun fichier n'est sélectionné
				if (inputRef.current) {
					inputRef.current.value = '';
				}
				return;
			}

			// Validation du format (PNG ou JPG uniquement)
			const validFormats = ['image/png', 'image/jpeg', 'image/jpg'];
			if (!validFormats.includes(selectedFile.type)) {
				errorNotify(
					commonTranslate('errors.imageFormatErrorTitle'),
					commonTranslate('errors.imageFormatErrorDescription')
				);
				// Réinitialiser l'input en cas d'erreur
				if (inputRef.current) {
					inputRef.current.value = '';
				}
				return;
			}

			// Validation de la taille (2 MB max)
			const maxSizeInBytes = 2 * 1024 * 1024; // 2 MB
			if (selectedFile.size > maxSizeInBytes) {
				errorNotify(
					commonTranslate('errors.imageFormatErrorTitle'),
					commonTranslate('errors.imageFormatErrorDescription')
				);
				// Réinitialiser l'input en cas d'erreur
				if (inputRef.current) {
					inputRef.current.value = '';
				}
				return;
			}

			const localStorageUser = JSON.parse(localStorage.getItem('user') || '');

			let base64Result: string;
			try {
				base64Result = await getBase64(selectedFile);
			} catch (error) {
				errorNotify(
					commonTranslate('errors.imageUploadErrorTitle'),
					commonTranslate('errors.imageUploadErrorDescription')
				);
				console.error('Error reading file:', error);
				// Réinitialiser l'input en cas d'erreur
				if (inputRef.current) {
					inputRef.current.value = '';
				}
				return;
			}

			// Extraire le préfixe Base64 correct selon le type de fichier
			let base64String: string;
			if (base64Result.startsWith('data:image/png;base64,')) {
				base64String = base64Result.replace('data:image/png;base64,', '');
			} else if (base64Result.startsWith('data:image/jpeg;base64,')) {
				base64String = base64Result.replace('data:image/jpeg;base64,', '');
			} else if (base64Result.startsWith('data:image/jpg;base64,')) {
				base64String = base64Result.replace('data:image/jpg;base64,', '');
			} else {
				// Fallback: essayer d'extraire après la virgule
				const commaIndex = base64Result.indexOf(',');
				if (commaIndex !== -1) {
					base64String = base64Result.substring(commaIndex + 1);
				} else {
					errorNotify(
						commonTranslate('errors.imageUploadErrorTitle'),
						commonTranslate('errors.imageUploadErrorDescription')
					);
					// Réinitialiser l'input en cas d'erreur
					if (inputRef.current) {
						inputRef.current.value = '';
					}
					return;
				}
			}

			setIsIconUploading(true);

			try {
				const updatedIcon = await dispatch(
					updateUserIcon({ id: user.id, img: base64String })
				).unwrap();

				setIcon(updatedIcon);

				await localStorage.setItem(
					'user',
					JSON.stringify({ ...localStorageUser, avatarLink: updatedIcon })
				);

				window.dispatchEvent(new Event('storage'));
				// Réinitialiser l'input après un succès pour permettre de sélectionner le même fichier à nouveau
				if (inputRef.current) {
					inputRef.current.value = '';
				}
			} catch (error) {
				errorNotify(
					commonTranslate('errors.imageUploadErrorTitle'),
					commonTranslate('errors.imageUploadErrorDescription')
				);
				console.error('Error uploading image:', error);
				// Réinitialiser l'input en cas d'erreur
				if (inputRef.current) {
					inputRef.current.value = '';
				}
			} finally {
				setIsIconUploading(false);
			}
		} catch (error) {
			errorNotify(
				commonTranslate('errors.imageUploadErrorTitle'),
				commonTranslate('errors.imageUploadErrorDescription')
			);
			console.error('Unexpected error:', error);
			// Réinitialiser l'input en cas d'erreur
			if (inputRef.current) {
				inputRef.current.value = '';
			}
		}
	};

	const handleActionDeleteModal = () => {
		setIsOpenDeleteModal((prev) => !prev);
	};

	const handleActionChangePasswordModal = () => {
		setIsOpenChangePasswordModal((prev) => !prev);
	};

	const handleUpdate = async (data: any) => {
		setUpdatingLoading(true);
		try {
			const updatedData = await dispatch(updateUserProfile({ id: user.id, data })).unwrap();

			setUser((prev: UserData) => ({ ...prev, ...data }));

			const localStorageUser = JSON.parse(localStorage.getItem('user') || '');
			const updatedLocalStorageUser = { ...localStorageUser, ...updatedData };
			await localStorage.setItem('user', JSON.stringify(updatedLocalStorageUser));

			window.dispatchEvent(new Event('storage'));

			await reset();

			getUserData();

			// Show success message
			successNotify(
				commonTranslate('errors.success'),
				commonTranslate('errors.successMessage')
			);
		} catch (error: any) {
			// Extract error message from the rejected value
			let errorMessage: string;
			
			if (typeof error === 'string') {
				// If error is directly a string
				errorMessage = error;
			} else if (error?.message) {
				// If error has a message property
				errorMessage = error.message;
			} else if (error?.response?.data) {
				// If error has response.data
				errorMessage = typeof error.response.data === 'string' 
					? error.response.data 
					: error.response.data?.message || error.response.data;
			} else if (error?.data) {
				// If error has data property
				errorMessage = typeof error.data === 'string' 
					? error.data 
					: error.data?.message || JSON.stringify(error.data);
			} else {
				// Fallback to default error message
				errorMessage = commonTranslate('errors.somethingWentWrongDescription');
			}
			
			// Display error in snackbar
			errorNotify(
				commonTranslate('errors.error'),
				errorMessage
			);
			
			console.log(error);
		} finally {
			setUpdatingLoading(false);
		}
	};

	const selectFile = () => {
		if (inputRef && inputRef.current) {
			inputRef.current.click();
		}
	};

	const handleDeleteIcon = async () => {
		setIsIconUploading(true);
		try {
			await dispatch(deleteUserIcon({ id: user.id })).unwrap();

			setIcon(null);

			const localStorageUser = JSON.parse(localStorage.getItem('user') || '');
			await localStorage.setItem(
				'user',
				JSON.stringify({ ...localStorageUser, avatarLink: null })
			);

			window.dispatchEvent(new Event('storage'));

			successNotify(
				commonTranslate('errors.success'),
				profileTranslate('text.avatarDeletedSuccess')
			);
		} catch (error) {
			errorNotify(
				commonTranslate('errors.error'),
				commonTranslate('errors.somethingWentWrongDescription')
			);
			console.error('Error deleting image:', error);
		} finally {
			setIsIconUploading(false);
		}
	};

	const handleDeleteAccount = async () => {
		try {
			const afterDelete = () => {
				dispatch(resetTickets());
				Object.keys(localStorage).forEach((key) => {
					if (key !== 'i18nextLng') {
						localStorage.removeItem(key);
					}
				});
				handleActionDeleteModal();
				navigate(ROUTES.LOGIN);
			};
			await dispatch(deleteCustomer()).unwrap();
			await dispatch(deleteUserAccount({ id: user.id, function: afterDelete })).unwrap();
		} catch (error) {
			console.log(error);
		}
	};

	return (
		<>
			<Styled.ChangeImage>
				<input
					ref={inputRef}
					type="file"
					id="file"
					onChange={handleFileChange}
					accept="image/png,image/jpeg,image/jpg"
					hidden
				/>
				{isIconUploading ? (
					<Styled.LoaderContainer>
						<Oval
							height={24}
							width={24}
							color="#fff"
							wrapperStyle={{}}
							wrapperClass=""
							visible={true}
							ariaLabel="oval-loading"
							secondaryColor="#91B6FF"
							strokeWidth={5}
							strokeWidthSecondary={5}
						/>
					</Styled.LoaderContainer>
				) : !icon ? (
					<Styled.UploadAvatar role="presentation" onClick={selectFile}>
						<Camera />
					</Styled.UploadAvatar>
				) : (
					<Styled.AvatarContainer>
						<Styled.UserAvatar
							alt="user-avatar"
							src={icon || ''}
							onClick={selectFile}
						/>
						<Styled.DeleteIconButton
							onClick={(e) => {
								e.stopPropagation();
								handleDeleteIcon();
							}}
							disabled={isIconUploading}
							title={profileTranslate('text.deleteAvatar') as string}
						>
							<Trash />
						</Styled.DeleteIconButton>
					</Styled.AvatarContainer>
				)}
			</Styled.ChangeImage>
			<form onSubmit={handleSubmit(handleUpdate)} style={{ width: '100%' }}>
				<Styled.Inputs className="rtl-able">
					<FormControl fullWidth error={!!errors.firstName}>
						<Typography sx={{ fontSize: '12px' }}>
							{signUpPageTranslate('text.firstName')}
						</Typography>
						<TextField
							inputProps={{ style: { padding: '10px 12px' } }}
							placeholder={signUpPageTranslate('placeholders.firstName') as string}
							sx={{
								height: '44px',
								'& .MuiOutlinedInput-root': {
									borderRadius: '8px',
								},
							}}
							{...register('firstName')}
						/>
						<FormHelperText
							sx={{
								fontSize: '12px',
								textAlign: 'right',
								marginRight: '12px',
								marginTop: '0',
							}}
						>
							{errors.firstName?.message}
						</FormHelperText>
					</FormControl>
					<FormControl fullWidth error={!!errors.lastName}>
						<Typography sx={{ fontSize: '12px' }}>
							{signUpPageTranslate('text.lastName')}
						</Typography>
						<TextField
							inputProps={{ style: { padding: '10px 12px' } }}
							placeholder={signUpPageTranslate('placeholders.lastName') as string}
							sx={{
								height: '44px',
								'& .MuiOutlinedInput-root': {
									borderRadius: '8px',
								},
							}}
							{...register('lastName')}
						/>
						<FormHelperText
							sx={{
								fontSize: '12px',
								textAlign: 'right',
								marginRight: '12px',
								marginTop: '0',
							}}
						>
							{errors.lastName?.message}
						</FormHelperText>
					</FormControl>
				</Styled.Inputs>
				{updatingLoading ? (
					<SubmitButton
						type="submit"
						variant="contained"
						color="primary"
						disabled={updatingLoading}
					>
						<Oval
							height={24}
							width={24}
							color="#fff"
							wrapperStyle={{}}
							wrapperClass=""
							visible={true}
							ariaLabel="oval-loading"
							secondaryColor="#91B6FF"
							strokeWidth={5}
							strokeWidthSecondary={5}
						/>
					</SubmitButton>
				) : (
					<SubmitButton
						type="submit"
						variant="contained"
						color="primary"
						// disabled
						disabled={!isDirty}
					>
						{commonTranslate('buttons.save-changes')}
					</SubmitButton>
				)}
			</form>
			<ChangeEmail />
			<Styled.ChangePassword className="rtl-able">
				<Styled.ChangePasswordInfo>
					<Typography variant="h4">{profileTranslate('text.changePasswordTitle')}</Typography>
					<Typography>{profileTranslate('text.changePasswordDescription')}</Typography>
				</Styled.ChangePasswordInfo>
				<Button onClick={handleActionChangePasswordModal}>
					{commonTranslate('buttons.change-password')}
				</Button>
			</Styled.ChangePassword>
			<Styled.DeleteButton
				sx={{ justifySelf: 'flex-end' }}
				className="rtl-able"
				onClick={handleActionDeleteModal}
			>
				<Trash />
				{commonTranslate('buttons.delete-account')}
			</Styled.DeleteButton>
			<DeleteAccountModal
				open={isOpenDeleteModal}
				handleClose={handleActionDeleteModal}
				handleConfirm={handleDeleteAccount}
			/>
			<ChangePasswordModal
				open={isOpenChangePasswordModal}
				onClose={handleActionChangePasswordModal}
			/>
		</>
	);
};
